package com.iregained.site.components.sections.home.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.product.styles.ChecklistInteractiveStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.dom.Div

@Composable
private fun Modifier.slideInLeft(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateX(if (isVisible) 0.px else (-40).px).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
private fun Modifier.scaleInSlow(isVisible: Boolean): Modifier {
    return this.opacity(if (isVisible) 1 else 0).scale(if (isVisible) 1f else 0.9f).transition(
        Transition.of("opacity", 1.2.s, TransitionTimingFunction.EaseOut),
        Transition.of("transform", 1.2.s, TransitionTimingFunction.EaseOut)
    )
}

@Composable
fun HomeFeaturesZigZagSection(data: HomeFeaturesZigZagSectionData = defaultHomeFeaturesZigZagSectionData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "home-features-zigzag"
    var isVisible by remember { mutableStateOf(false) }

    if (data.features.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(Colors.White)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex).justifyContent(JustifyContent.Center)
                    .toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element)
                            onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1440.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (data.sectionHeader.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionHeader,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 16.px).slideInLeft(isVisible, 0),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    if (data.sectionDescription.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().maxWidth(1200.px).fontSize(18.px)
                                .fontWeight(FontWeight.Medium).color(palette.custom.darkText)
                                .textAlign(TextAlign.Center).margin(bottom = 40.px).slideInLeft(isVisible, 150)
                                .toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML = data.sectionDescription; onDispose {}
                                    }
                                })
                    }

                    if (isDesktop) {
                        Row(
                            modifier = Modifier.fillMaxWidth().gap(100.px),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            BenefitsChecklist(data.features, data.checklistIconPath, Modifier.weight(1f), isVisible)
                            if (data.mainImagePath.isNotBlank()) {
                                Image(
                                    src = data.mainImagePath,
                                    modifier = Modifier.unextractable().weight(1f).fillMaxWidth().height(800.px)
                                        .borderRadius(12.px).objectFit(ObjectFit.Cover).scaleInSlow(isVisible)
                                )
                            }
                        }
                    } else {
                        Column(modifier = Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
                            BenefitsChecklist(
                                data.features,
                                data.checklistIconPath,
                                Modifier.fillMaxWidth().margin(bottom = 40.px),
                                isVisible
                            )
                            Image(
                                src = data.mainImagePath,
                                modifier = Modifier.unextractable().fillMaxWidth().height(400.px).borderRadius(12.px)
                                    .objectFit(ObjectFit.Cover).scaleInSlow(isVisible)
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun BenefitsChecklist(
    checklist: List<ProductFeatureData>, iconPath: String, modifier: Modifier, isVisible: Boolean
) {
    Column(modifier = modifier.gap(4.px)) {
        checklist.forEachIndexed { index, item ->
            key(item.title) {
                ChecklistItem(
                    title = item.title,
                    desc = item.description,
                    iconPath = iconPath,
                    modifier = Modifier.slideInLeft(isVisible, 300 + (index * 150))
                )
            }
        }
    }
}

@Composable
private fun ChecklistItem(title: String, desc: String, iconPath: String, modifier: Modifier = Modifier) {
    val palette = ColorMode.current.toSitePalette()
    Row(
        verticalAlignment = Alignment.Top,
        modifier = ChecklistInteractiveStyle.toModifier().then(modifier).fillMaxWidth()
    ) {
        if (iconPath.isNotBlank()) Image(
            src = iconPath, modifier = Modifier.unextractable().size(24.px).margin(right = 16.px, top = 2.px)
        )
        Column {
            if (title.isNotBlank()) {
                Div(
                    attrs = Modifier.color(palette.brand.primary).fontWeight(FontWeight.Bold).toAttrs {
                        ref { htmlElement -> htmlElement.innerHTML = title; onDispose {} }
                    })
            }
            if (desc.isNotBlank()) Div(
                attrs = Modifier.color(palette.custom.darkText).fontSize(16.px).lineHeight(1.5)
                    .toAttrs { ref { htmlElement -> htmlElement.innerHTML = desc; onDispose {} } })
        }
    }
}

/**
 * CMS IMAGE UPLOAD SPECS:
 * @field mainImagePath:
 * - Aspect Ratio: Flexible (Landscape/Portrait).
 * - Recommended Dimensions: 600x600px.
 * - Max File Size: 300KB.
 * - Behavior: Cropped to 500px height (Desktop) via ObjectFit.Cover.
 * @field checklistIconPath:
 * - Aspect Ratio: 1:1.
 * - Recommended Dimensions: 24x24px.
 * - Format: SVG ONLY.
 */
data class ProductFeatureData(val title: String, val description: String)

data class HomeFeaturesZigZagSectionData(
    val sectionId: String? = null,
    val sectionHeader: String,
    val sectionDescription: String,
    val mainImagePath: String,
    val checklistIconPath: String,
    val features: List<ProductFeatureData>
)

val defaultHomeFeaturesZigZagSectionData = HomeFeaturesZigZagSectionData(
    sectionId = "home-features-zigzag",
    sectionHeader = "FEATURES & BENEFITS",
    sectionDescription = "By leveraging the principles of neuroplasticity to actively retrain the brain, rather than merely exercising the muscles, the MyHand<sup>®</sup> approach unlocks the ultimate benefit of true, long-term hand rehabilitation.",
    mainImagePath = Res.Images.Home.Product.DEVICE_IMG,
    checklistIconPath = Res.Images.ProductPage.Neuroscience.ICON_CHECK,
    features = listOf(
        ProductFeatureData(
            title = "Patient centric design",
            description = "Features multiple degrees of freedom for adjustments. Accommodates to a wide range of patients with varying abilities, function, and spasticity levels.",
        ), ProductFeatureData(
            title = "Intelligent system",
            description = "Smart algorithms device built on highly effective clinical protocols. Demonstrated clinical efficacy in several studies.",
        ), ProductFeatureData(
            title = "Quick & Easy to set-up",
            description = "Ready to use out of the box. Designed to increase therapy time and improve productivity.",
        ), ProductFeatureData(
            title = "Neuroplasticity Enhancing",
            description = "Protocols developed through scientific research on neuroplasticity for motor control recovery. Enables effective hand function rehabilitation and facilitates ADL and grasp specific training.",
        ), ProductFeatureData(
            title = "Multi-modal Therapy",
            description = "The MyHand<sup>®</sup> Application has pre-programmed protocols, games, and custom protocols. Caters to a diverse range of patient engagement levels and conditions for optimal therapy adherence.",
        ), ProductFeatureData(
            title = "IOMT platform",
            description = "Cloud connected HIPAA/PHIPA compliant platform. Allows secure and compliant storage of data to facilitate therapy management, data analytics, and reporting.",
        ), ProductFeatureData(
            title = "Portable System",
            description = "Mounted on a maneuverable stand with multiple adjustments. Easy to use, quick set up time, highly adjustable and easy to roll from one location to another.",
        )
    )
)
